import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronDown, Layers, Pencil, Search } from "lucide-react";
import { MAIN_URL } from "../../../Configurations/Urls";
import "./BatchModulesGroupedView.css";

const STATUS_BADGE = {
  Completed: "green",
  Ongoing: "neutral",
  "On Hold": "amber",
  Skipped: "red",
};

function formatWhen(value) {
  if (!value) return "—";
  const text = String(value).trim();
  if (!text || text === "-") return "—";
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}-${match[2]}-${match[1]}`;
  return text;
}

function moduleName(row) {
  return (
    row?.program_module?.module_name ||
    row?.programModule?.module_name ||
    "Module"
  );
}

function batchLabel(row) {
  return row?.batch?.batch_code || row?.batch?.batch_name || "Batch";
}

function groupModules(rows) {
  const groups = new Map();
  rows.forEach((row) => {
    const batchId =
      row?.organization_ems_batch_id ??
      row?.batch?.organization_ems_batch_id ??
      "none";
    if (!groups.has(batchId)) {
      groups.set(batchId, {
        id: batchId,
        label: batchLabel(row),
        program:
          row?.batch?.training_program?.training_program_name ||
          row?.batch?.training_program_name ||
          "",
        modules: [],
      });
    }
    groups.get(batchId).modules.push(row);
  });

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      modules: [...group.modules].sort(
        (a, b) => Number(a?.sequence_no || 0) - Number(b?.sequence_no || 0)
      ),
    }))
    .sort((a, b) => String(a.label).localeCompare(String(b.label)));
}

export default function BatchModulesGroupedView({ organizationId }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!organizationId) {
      setRows([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get(`${MAIN_URL}/api/ems/batch-modules`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          params: { organization_id: organizationId, per_page: "all" },
        });
        const data = Array.isArray(response?.data?.data) ? response.data.data : [];
        if (!cancelled) setRows(data);
      } catch (err) {
        if (!cancelled) {
          setRows([]);
          setError(err?.response?.data?.message || "Could not load batch modules.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const grouped = groupModules(rows);
    if (!needle) return grouped;
    return grouped
      .map((group) => {
        const batchHit = String(group.label).toLowerCase().includes(needle);
        const modules = batchHit
          ? group.modules
          : group.modules.filter((row) =>
              moduleName(row).toLowerCase().includes(needle)
            );
        return { ...group, modules };
      })
      .filter((group) => group.modules.length > 0);
  }, [rows, query]);

  const toggle = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <section className="bm-board">
      <div className="ems-toolbar">
        <label className="ems-search bm-search">
          <Search size={16} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search batch or module"
          />
        </label>
        <span className="bm-count">
          {groups.length} batch{groups.length === 1 ? "" : "es"}
        </span>
      </div>

      {loading ? (
        <div className="ems-card ems-empty">Loading batch modules…</div>
      ) : null}
      {!loading && error ? <div className="ems-card ems-empty">{error}</div> : null}
      {!loading && !error && groups.length === 0 ? (
        <div className="ems-card ems-empty">
          <div className="ems-empty-icon">
            <Layers size={22} />
          </div>
          No batch modules found.
        </div>
      ) : null}

      {!loading &&
        groups.map((group) => {
          const isClosed = !expanded[group.id];
          const completed = group.modules.filter((row) => row.status === "Completed").length;
          const ongoing = group.modules.filter((row) => row.status === "Ongoing").length;
          const progress = group.modules.length
            ? Math.round((completed / group.modules.length) * 100)
            : 0;
          return (
            <article key={group.id} className="ems-card bm-batch">
              <button type="button" className="bm-batch-head" onClick={() => toggle(group.id)}>
                <span className="ems-stat-icon">
                  <Layers size={18} />
                </span>
                <div className="bm-batch-copy">
                  <div className="bm-batch-title">
                    <span className="bm-batch-name">{group.label}</span>
                    {ongoing > 0 ? <span className="ems-badge neutral">{ongoing} ongoing</span> : null}
                  </div>
                  {group.program ? <div className="bm-batch-program">{group.program}</div> : null}
                  <div className="bm-progress" aria-hidden="true">
                    <span style={{ width: `${progress}%` }} />
                  </div>
                </div>
                <div className="bm-batch-meta">
                  <strong>
                    {completed}/{group.modules.length}
                  </strong>
                  <span>completed</span>
                </div>
                <ChevronDown size={18} className={isClosed ? "bm-chevron is-closed" : "bm-chevron"} />
              </button>

              {!isClosed ? (
                <ol className="bm-modules">
                  {group.modules.map((row, index) => {
                    const status = row.status || "Not Started";
                    const badge = STATUS_BADGE[status];
                    const isLast = index === group.modules.length - 1;
                    return (
                      <li
                        key={row.organization_ems_batch_module_id}
                        className={isLast ? "bm-module is-last" : "bm-module"}
                      >
                        <span className={`bm-seq${status === "Completed" ? " is-done" : ""}${status === "Ongoing" ? " is-live" : ""}`}>
                          {row.sequence_no ?? "—"}
                        </span>
                        <div className="bm-module-main">
                          <div className="bm-module-name">{moduleName(row)}</div>
                          <div className="bm-module-dates">
                            Started {formatWhen(row.started_at)}
                            <span className="bm-dot" />
                            Completed {formatWhen(row.completed_at)}
                          </div>
                        </div>
                        <span className={badge ? `ems-badge ${badge}` : "ems-badge bm-badge-muted"}>
                          {status}
                        </span>
                        <button
                          type="button"
                          className="ems-icon-btn bm-edit"
                          aria-label="Edit module"
                          onClick={() =>
                            navigate(`/batch-modules/edit/${row.organization_ems_batch_module_id}`)
                          }
                        >
                          <Pencil size={15} />
                        </button>
                      </li>
                    );
                  })}
                </ol>
              ) : null}
            </article>
          );
        })}
    </section>
  );
}
